export interface FaqCategory {
  id: string;
  labelKey: string;
}

export interface FaqItem {
  cat: string;
  qKey: string;
  aKey: string;
}

export const FAQ_CATEGORIES: FaqCategory[] = [
  { id: "account", labelKey: "helpCenter.faqCatAccount" },
  { id: "apply", labelKey: "helpCenter.faqCatApply" },
  { id: "license", labelKey: "helpCenter.faqCatLicense" },
  { id: "renewal", labelKey: "helpCenter.faqCatRenewal" },
  { id: "integration", labelKey: "helpCenter.faqCatIntegration" },
];

export const FAQ_ITEMS: FaqItem[] = [
  { cat: "account", qKey: "helpCenter.q1", aKey: "helpCenter.a1" },
  { cat: "account", qKey: "helpCenter.q2", aKey: "helpCenter.a2" },
  { cat: "account", qKey: "helpCenter.q3", aKey: "helpCenter.a3" },
  { cat: "apply", qKey: "helpCenter.q4", aKey: "helpCenter.a4" },
  { cat: "apply", qKey: "helpCenter.q5", aKey: "helpCenter.a5" },
  { cat: "apply", qKey: "helpCenter.q6", aKey: "helpCenter.a6" },
  { cat: "apply", qKey: "helpCenter.q7", aKey: "helpCenter.a7" },
  { cat: "license", qKey: "helpCenter.q8", aKey: "helpCenter.a8" },
  { cat: "license", qKey: "helpCenter.q9", aKey: "helpCenter.a9" },
  { cat: "license", qKey: "helpCenter.q10", aKey: "helpCenter.a10" },
  { cat: "license", qKey: "helpCenter.q11", aKey: "helpCenter.a11" },
  { cat: "renewal", qKey: "helpCenter.q12", aKey: "helpCenter.a12" },
  { cat: "renewal", qKey: "helpCenter.q13", aKey: "helpCenter.a13" },
  { cat: "renewal", qKey: "helpCenter.q14", aKey: "helpCenter.a14" },
  { cat: "integration", qKey: "helpCenter.q15", aKey: "helpCenter.a15" },
  { cat: "integration", qKey: "helpCenter.q16", aKey: "helpCenter.a16" },
  { cat: "integration", qKey: "helpCenter.q17", aKey: "helpCenter.a17" },
];

export const FAQ_CATEGORY_COUNTS: Record<string, number> = FAQ_CATEGORIES.reduce(
  (acc, category) => {
    acc[category.id] = FAQ_ITEMS.filter((item) => item.cat === category.id).length;
    return acc;
  },
  {} as Record<string, number>,
);

/** Maps the ticket form issue-type i18n key to a FAQ category id. */
export const ISSUE_TYPE_CATEGORY: Record<string, string> = {
  "helpCenter.formTypeAccount": "account",
  "helpCenter.formTypeApply": "apply",
  "helpCenter.formTypeLicense": "license",
  "helpCenter.formTypeRenewal": "renewal",
  "helpCenter.formTypeIntegration": "integration",
};

/** Maps a FAQ category id back to the ticket form issue-type i18n key. */
export const CATEGORY_ISSUE_TYPE: Record<string, string> = {
  account: "helpCenter.formTypeAccount",
  apply: "helpCenter.formTypeApply",
  license: "helpCenter.formTypeLicense",
  renewal: "helpCenter.formTypeRenewal",
  integration: "helpCenter.formTypeIntegration",
};

/** DOM anchor id for a FAQ item, safe to use inside a URL hash. */
export function faqAnchor(qKey: string): string {
  return `faq-item-${qKey.replace(/\./g, "-")}`;
}

/** 「待优化」阈值的默认值（可由运营在后台调整）。 */
export const FAQ_OPTIMIZE_RATE_THRESHOLD = 0.5;

/** 默认至少累计这么多条「没帮助」才会进入「待优化」，避免单条噪音误报。 */
export const FAQ_OPTIMIZE_MIN_NOT_HELPFUL = 2;

/** 阈值允许范围，用于后台校验与输入约束。 */
export const FAQ_OPTIMIZE_RATE_MIN = 0;
export const FAQ_OPTIMIZE_RATE_MAX = 1;
export const FAQ_OPTIMIZE_MIN_NOT_HELPFUL_MAX = 999;

/**
 * 「待优化」判定：没帮助占比过高，且没帮助累计达到最小条数。
 * 阈值可传入，默认回落到内置常量，方便运营在后台按自己的节奏定义。
 */
export function isHighNotHelpful(
  notHelpful: number,
  total: number,
  rateThreshold: number = FAQ_OPTIMIZE_RATE_THRESHOLD,
  minNotHelpful: number = FAQ_OPTIMIZE_MIN_NOT_HELPFUL,
): boolean {
  if (total <= 0) return false;
  const safeRate = Number.isFinite(rateThreshold) ? rateThreshold : FAQ_OPTIMIZE_RATE_THRESHOLD;
  const safeMin = Number.isFinite(minNotHelpful) ? minNotHelpful : FAQ_OPTIMIZE_MIN_NOT_HELPFUL;
  return notHelpful >= safeMin && notHelpful / total >= safeRate;
}

/** 指向帮助中心某条 FAQ 的预览地址（带锚点，打开即自动展开并定位）。 */
export function faqPreviewUrl(qKey: string): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const prefix = basePath ? `/${basePath}` : "";
  return `${window.location.origin}${prefix}/help#${faqAnchor(qKey)}`;
}

/**
 * Returns the most relevant FAQ items for a category (defaults to 3),
 * putting recently-viewed questions first so high-frequency confusions surface on top.
 */
export function getRelatedFaqs(categoryId: string, limit = 3, recentKeys: string[] = []): FaqItem[] {
  if (!categoryId) return [];
  const items = FAQ_ITEMS.filter((item) => item.cat === categoryId);
  if (recentKeys.length === 0) return items.slice(0, limit);
  const recentSet = new Set(recentKeys);
  const prioritized = [
    ...items.filter((item) => recentSet.has(item.qKey)),
    ...items.filter((item) => !recentSet.has(item.qKey)),
  ];
  return prioritized.slice(0, limit);
}